import {
  OnInit,
  Input,
  Output,
  Directive,
  OnDestroy,
  ElementRef,
  EventEmitter,
  HostListener,
} from '@angular/core';

import {
  SortableResult,
  SortableEngineService,
} from '../services/sortable-engine.service';

@Directive({
  selector: '[appSortableList]',
  standalone: true,
  providers: [SortableEngineService],
})
export class SortableListDirective implements OnInit, OnDestroy {
  @Input() appSortableList: any[] = [];
  // * CSS selector to handle drag items - use '.drag-handle' for drag handle
  @Input() sortableHandle?: string;
  @Input() sortableDisabled = false;
  @Input() dragThreshold = 5;
  @Input() dragDelay = 150;

  // * auto-scroll config
  @Input() scrollSpeed = 10;
  @Input() scrollSensitivity = 60;

  @Output() sorted = new EventEmitter<SortableResult>();

  private isDragCandidate = false;
  private startPointer = { x: 0, y: 0 };
  private dragTimer: number | null = null;
  private pendingEvent: PointerEvent | null = null;

  private container!: HTMLElement;
  private autoScrollActive = false;
  private autoScrollDirection = 0;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private sortableEngine: SortableEngineService,
  ) {}

  ngOnInit(): void {
    this.sortableEngine.onSorted = (result: SortableResult) => {
      this.sorted.emit(result);
    };

    this.container = this.elementRef.nativeElement;
  }

  ngOnDestroy(): void {
    this.sortableEngine.onSorted = null;
    this.cleanupDragCandidate();
    this.stopAutoScroll();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (this.sortableDisabled) return;

    // Only start drag from primary button (left click or touch)
    if (event.button !== 0) {
      return;
    }

    // * check if click is on interactive element that should not trigger drag
    if (this.isInteractiveElement(event.target as HTMLElement)) {
      return;
    }

    // * setup drag candidate - don't prevent default yet
    this.isDragCandidate = true;
    this.startPointer = { x: event.clientX, y: event.clientY };
    this.pendingEvent = event;

    // * setup listeners for movement detection
    document.addEventListener('pointermove', this.onCandidateMove);
    document.addEventListener('pointerup', this.onCandidateUp);

    // * setup delay timer
    this.dragTimer = window.setTimeout(() => {
      if (this.isDragCandidate && this.pendingEvent) {
        this.startDrag(this.pendingEvent);
      }
    }, this.dragDelay);
  }

  private onCandidateMove = (event: PointerEvent) => {
    if (!this.isDragCandidate || !this.pendingEvent) return;

    const deltaX = Math.abs(event.clientX - this.startPointer.x);
    const deltaY = Math.abs(event.clientY - this.startPointer.y);

    // * if moved beyond threshold, start drag immediately
    if (deltaX > this.dragThreshold || deltaY > this.dragThreshold) {
      this.startDrag(this.pendingEvent);
    }
  };

  private onCandidateUp = (event: PointerEvent) => {
    // * just a click
    this.cleanupDragCandidate();
  };

  private startDrag(event: PointerEvent): void {
    if (!this.isDragCandidate) return;

    // * now prevent default since we're starting actual drag
    event.preventDefault();

    const success = this.sortableEngine.start(
      event,
      this.container,
      this.appSortableList,
      this.sortableHandle,
    );

    if (success) {
      // * visual feedback to container - optional
      this.container.classList.add('sortable-active');

      // * listen for drag move for auto-scroll
      document.addEventListener('pointermove', this.onDragMove);

      // * listen for drag end to remove visual feedback
      const cleanup = () => {
        this.container.classList.remove('sortable-active');
        document.removeEventListener('pointerup', cleanup);
        document.removeEventListener('pointermove', this.onDragMove);
        this.stopAutoScroll();
      };

      document.addEventListener('pointerup', cleanup);
    }

    this.cleanupDragCandidate();
  }

  private cleanupDragCandidate(): void {
    this.isDragCandidate = false;
    this.pendingEvent = null;

    if (this.dragTimer) {
      clearTimeout(this.dragTimer);
      this.dragTimer = null;
    }

    document.removeEventListener('pointermove', this.onCandidateMove);
    document.removeEventListener('pointerup', this.onCandidateUp);
  }

  private isInteractiveElement(element: HTMLElement): boolean {
    if (!element) return false;

    // * check if element or any parent is interactive
    let current = element;
    while (current && current !== this.elementRef.nativeElement) {
      const tagName = current.tagName.toLowerCase();

      // * form elements
      if (['input', 'button', 'select', 'textarea', 'a'].includes(tagName)) {
        return true;
      }

      // * specific interactive elements we want to protect
      if (current.closest('sui-button')) {
        return true;
      }

      current = current.parentElement!;
    }

    return false;
  }

  private onDragMove = (event: PointerEvent) => {
    const rect = this.container.getBoundingClientRect();

    const topEdge = rect.top + this.scrollSensitivity;
    const bottomEdge = rect.bottom - this.scrollSensitivity;

    let direction = 0;

    if (event.clientY < topEdge) {
      direction = -1;
    } else if (event.clientY > bottomEdge) {
      direction = 1;
    }

    if (direction !== 0) {
      this.startAutoScroll(direction);
    } else {
      this.stopAutoScroll();
    }
  };

  private startAutoScroll(direction: number) {
    if (this.autoScrollActive && this.autoScrollDirection === direction) return;

    this.autoScrollDirection = direction;
    this.autoScrollActive = true;

    requestAnimationFrame(this.autoScrollLoop);
  }

  private stopAutoScroll() {
    this.autoScrollActive = false;
  }

  private autoScrollLoop = () => {
    if (!this.autoScrollActive) return;

    this.container.scrollTop += this.autoScrollDirection * this.scrollSpeed;

    requestAnimationFrame(this.autoScrollLoop);
  };

  get isDragging(): boolean {
    return this.sortableEngine.dragging;
  }
}
